// Soft white-label brand theme engine (WL).
//
// Maps a tenant's `branding.accent_hex` onto the runtime CSS custom properties that back the `primary`
// and `accent` Tailwind tokens, so EVERY `*-primary` / `*-accent` utility across the app re-themes with
// zero per-component changes.
//
// AA is GUARANTEED against WCAG 2 AA (4.5:1) — the same standard the axe gate enforces:
//  - The primary FILL is darkened toward black until WHITE text reaches 4.5:1 on it, and the foreground
//    is fixed to white. So every `bg-primary` surface — including the many that hardcode `text-white` —
//    is AA for ANY brand colour (a saturated mid-tone like saffron/gold/sky becomes a richer shade
//    rather than an unreadable one). The darker hover (`bg-primary-700`) only raises contrast. Holds in
//    light AND dark themes (white-on-dark-fill is theme-independent).
//  - The accent-AS-TEXT variants (`text-accent`) are pushed darker on a light surface / lighter on a
//    dark one until they reach 4.5:1 on that surface.
//
// High-contrast theme ('hc') is left untouched — its guaranteed palette wins over brand. Un-branded
// tenants clear the overrides → the index.css/tokens.css royal defaults apply, byte-identical to stock.
import { hexToRgb } from "./contrast";
import { withTransitionsSuppressed } from "./theme";

const DEFAULT_ACCENT = "#1D4ED8";
const TARGET = 4.6;         // WCAG 2 AA normal-text ratio (4.5:1, what axe checks) + a tiny margin
const WHITE = [255, 255, 255];
const STOCK_SURFACES = {
  light: {
    sidebar: [246, 249, 255],
    page: [241, 245, 249],
    card: WHITE,
    textFamily: [
      [49, 51, 56],
      [86, 91, 100],
      [91, 107, 127],
      [92, 106, 126],
    ],
  },
  dark: {
    sidebar: [11, 24, 43],
    page: [30, 41, 59],
    card: [17, 26, 46],
    raised: [26, 37, 64],
    textFamily: [
      [246, 246, 245],
      [211, 217, 224],
      [166, 180, 200],
      [150, 163, 184],
    ],
  },
};

const BRAND_VARS = [
  "--vp-primary", "--vp-primary-50", "--vp-primary-100", "--vp-primary-600", "--vp-primary-700",
  "--vp-primary-fg", "--vp-accent", "--vp-accent-hover", "--vp-accent-active", "--vp-accent-fg",
  "--vp-fg-on-accent", "--vp-ring",
];
// Branding v2 — chrome-surface overrides (sidebar / page / card / border). Light fields drive the
// light/sepia themes, dark_overrides drives dark, and high contrast always keeps the stock palette.
const SURFACE_VARS = [
  "--vp-bg-canvas-warm", "--vp-bg-muted", "--vp-bg-surface", "--vp-bg-surface-raised",
  "--vp-border-default", "--vp-border-subtle", "--vp-fg-default", "--vp-fg-secondary",
  "--vp-fg-subtle", "--vp-fg-faint",
  "--vp-font-heading", "--vp-font-body",
];
const CSS_TAG_ID = "vp-brand-css";
// Branding v3 typography: whitelisted stacks — Sora/Inter are pre-loaded in index.html; the
// Manrope/Poppins override faces load ON DEMAND via _ensureGoogleFont the first time a tenant
// brand selects one (perf: the marketing/app entry no longer ships 4 families × 19 weights).
const FONT_STACKS = {
  manrope: '"Manrope", "Inter", system-ui, sans-serif',
  poppins: '"Poppins", "Inter", system-ui, sans-serif',
  inter: '"Inter", system-ui, sans-serif',
  system: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  serif: 'Georgia, "Times New Roman", serif',
};
// Whitelisted, once-only Google Fonts injection for the override faces (font-display: swap, so
// text renders in the Inter fallback until the face arrives — no invisible-text flash).
const GOOGLE_FONT_URLS = {
  manrope: "https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap",
  poppins: "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap",
};
function _ensureGoogleFont(key) {
  const href = GOOGLE_FONT_URLS[key];
  if (!href || typeof document === "undefined") return;
  const id = `vp-brand-font-${key}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = href;
  document.head.appendChild(link);
}

const clamp = (n) => Math.max(0, Math.min(255, Math.round(n)));
const scale = (rgb, f) => rgb.map((c) => clamp(c * f));
const tint = (rgb, amt) => rgb.map((c) => clamp(c + (255 - c) * amt));  // mix toward white
const triplet = (rgb) => rgb.join(" ");
const isHex = (s) => typeof s === "string" && /^#[0-9a-fA-F]{6}$/.test(s);

// WCAG relative luminance + contrast ratio (matches axe's color-contrast check).
const _lin = (c) => { const s = c / 255; return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4; };
const relLum = ([r, g, b]) => 0.2126 * _lin(r) + 0.7152 * _lin(g) + 0.0722 * _lin(b);
function contrast(a, b) {
  const l1 = relLum(a), l2 = relLum(b), hi = Math.max(l1, l2), lo = Math.min(l1, l2);
  return (hi + 0.05) / (lo + 0.05);
}

// Darken the fill toward black until WHITE text reaches AA on it. Always converges (black = 21:1).
function aaFill(base) {
  let fill = base.slice();
  for (let i = 0; i < 48 && contrast(WHITE, fill) < TARGET; i++) fill = scale(fill, 0.92);
  return fill;
}

// Keep an arbitrary tenant surface in the selected theme's polarity. The app has one shared text
// token stack, so a black card inside the light theme (or a white card inside the dark theme) cannot
// be made safe without creating a second inverse-content system. Shift only as far as required for
// every stock text tier to retain normal-text AA contrast.
function aaSurface(rgb, dark) {
  const foregrounds = dark ? STOCK_SURFACES.dark.textFamily : STOCK_SURFACES.light.textFamily;
  let cur = rgb.slice();
  for (let i = 0; i < 64 && !meetsTextContrastOnEverySurface(cur, foregrounds); i++) {
    cur = dark ? scale(cur, 0.9) : tint(cur, 0.1);
  }
  return cur;
}

function meetsTextContrastOnEverySurface(rgb, surfaces) {
  return surfaces.every((surface) => contrast(rgb, surface) >= TARGET);
}

// Push a brand colour until it reaches AA as TEXT on every rendered chrome surface: darken in the
// light theme, lighten in the dark theme. Surface normalization above guarantees convergence.
function aaTextOnSurfaces(rgb, surfaces, dark) {
  let cur = rgb.slice();
  for (let i = 0; i < 48 && !meetsTextContrastOnEverySurface(cur, surfaces); i++) {
    cur = dark ? cur.map((c) => clamp(c * 1.1 + 10)) : scale(cur, 0.9);
  }
  return cur;
}

function foregroundFamily(rgb, surfaces, dark) {
  const base = aaTextOnSurfaces(rgb, surfaces, dark);
  return [0.12, 0.22, 0.3].reduce((family, amount) => {
    const previous = family[family.length - 1];
    const softened = dark ? scale(base, 1 - amount) : tint(base, amount);
    const corrected = aaTextOnSurfaces(softened, surfaces, dark);
    const preservesHierarchy = dark
      ? relLum(corrected) <= relLum(previous)
      : relLum(corrected) >= relLum(previous);
    family.push(preservesHierarchy ? corrected : previous);
    return family;
  }, [base]);
}

let _branding = null;        // last applied branding (re-applied on theme change)
let _observing = false;

function _injectCss(css) {
  let tag = document.getElementById(CSS_TAG_ID);
  if (!css) { if (tag) tag.remove(); return; }
  if (!tag) {
    tag = document.createElement("style");
    tag.id = CSS_TAG_ID;
    document.head.appendChild(tag);
  }
  tag.textContent = css;   // server-sanitized; textContent can never create elements
}

function _apply() {
  const el = document.documentElement;
  const b = _branding;
  const theme = el.getAttribute("data-theme") || "light";
  const clearAll = () => {
    BRAND_VARS.forEach((v) => el.style.removeProperty(v));
    SURFACE_VARS.forEach((v) => el.style.removeProperty(v));
    _injectCss("");
  };
  // No brand at all — OR the high-contrast theme (its guaranteed palette is non-negotiable).
  if (!b || theme === "hc") { clearAll(); return; }
  clearAll();
  const dark = theme === "dark";
  const set = (k, val) => el.style.setProperty(k, triplet(val));
  const ov = dark ? (b.dark_overrides || {}) : b;
  const stock = dark ? STOCK_SURFACES.dark : STOCK_SURFACES.light;
  const surface = {
    sidebar: isHex(ov.sidebar_bg_hex) ? aaSurface(hexToRgb(ov.sidebar_bg_hex), dark) : stock.sidebar,
    page: isHex(ov.page_bg_hex) ? aaSurface(hexToRgb(ov.page_bg_hex), dark) : stock.page,
    card: isHex(ov.card_bg_hex) ? aaSurface(hexToRgb(ov.card_bg_hex), dark) : stock.card,
    raised: isHex(ov.card_bg_hex) ? aaSurface(hexToRgb(ov.card_bg_hex), dark) : (stock.raised || stock.card),
  };
  const contentSurfaces = [surface.sidebar, surface.page, surface.card, surface.raised];

  // ---- accent + primary controls (buttons/links/rings) ----
  // v2: an explicit button colour drives the PRIMARY fill; the accent drives brand-as-text. Either
  // alone drives both (so accent-only tenants keep the original behavior).
  const fillHex = isHex(ov.button_hex) ? ov.button_hex : (isHex(b.button_hex) ? b.button_hex : b.accent_hex);
  const textHex = (isHex(b.accent_hex) && b.accent_hex.toUpperCase() !== DEFAULT_ACCENT)
    ? b.accent_hex : fillHex;
  if (isHex(fillHex) && (fillHex.toUpperCase() !== DEFAULT_ACCENT || isHex(ov.button_hex) || isHex(b.button_hex))) {
    const fill = aaFill(hexToRgb(fillHex));    // AA-corrected primary fill (white-on-fill ≥ 4.5:1)
    const text = aaTextOnSurfaces(hexToRgb(textHex), contentSurfaces, dark);
    set("--vp-primary", fill);
    set("--vp-primary-50", tint(fill, 0.92));  // light tints derived from the brand (was royal-blue)
    set("--vp-primary-100", tint(fill, 0.84));
    set("--vp-primary-600", scale(fill, 0.92));
    set("--vp-primary-700", scale(fill, 0.84));
    set("--vp-primary-fg", WHITE);
    set("--vp-accent", text);
    set("--vp-accent-hover", scale(text, dark ? 1.08 : 0.92));
    set("--vp-accent-active", text);
    set("--vp-accent-fg", WHITE);
    set("--vp-fg-on-accent", WHITE);
    set("--vp-ring", fill);                     // AA-corrected → the focus ring stays visible on light bg
  }

  // ---- chrome surfaces (Branding v2/v3) — the light columns drive the light theme; the
  // dark_overrides set (same keys) drives the dark theme. Unset keys keep the stock palette.
  if (isHex(ov.sidebar_bg_hex)) set("--vp-bg-canvas-warm", surface.sidebar);
  if (isHex(ov.page_bg_hex)) set("--vp-bg-muted", surface.page);
  if (isHex(ov.card_bg_hex)) {
    set("--vp-bg-surface", surface.card);
    set("--vp-bg-surface-raised", surface.card);
  }
  if (isHex(ov.border_hex)) {
    set("--vp-border-default", hexToRgb(ov.border_hex));
    set("--vp-border-subtle", tint(hexToRgb(ov.border_hex), 0.45));
  }
  // Letter/text colour — pushed to AA against the active page background so a light grey pick
  // can't produce unreadable text on the sidebar, page or card surfaces.
  if (isHex(ov.text_hex)) {
    const [defaultText, secondaryText, subtleText, faintText] = foregroundFamily(
      hexToRgb(ov.text_hex),
      contentSurfaces,
      dark,
    );
    set("--vp-fg-default", defaultText);
    set("--vp-fg-secondary", secondaryText);
    set("--vp-fg-subtle", subtleText);
    set("--vp-fg-faint", faintText);
  }

  // ---- typography (both themes) ----
  const setRaw = (k, val) => el.style.setProperty(k, val);
  if (FONT_STACKS[b.font_heading]) { _ensureGoogleFont(b.font_heading); setRaw("--vp-font-heading", FONT_STACKS[b.font_heading]); }
  if (FONT_STACKS[b.font_body]) { _ensureGoogleFont(b.font_body); setRaw("--vp-font-body", FONT_STACKS[b.font_body]); }

  // ---- advanced CSS (server-sanitized in routers/whitelabel.py) ----
  _injectCss(b.custom_css || "");
}

// Re-theme when the colour scheme flips (light↔dark↔hc) so brand stays AA on the new surface.
function _ensureThemeObserver() {
  if (_observing || typeof MutationObserver === "undefined") return;
  _observing = true;
  new MutationObserver(() => _apply()).observe(document.documentElement, {
    attributes: true, attributeFilter: ["data-theme"],
  });
}

/** Apply a tenant's branding to the running app (idempotent). Pass null/default to revert to royal.
 * The var swap touches every accent/surface token at once — suppressed the same way a theme swap is,
 * so a live Branding save repaints instantly instead of smearing through every colour transition. */
export function applyBrand(branding) {
  if (typeof document === "undefined") return;
  _branding = branding || null;
  _ensureThemeObserver();
  withTransitionsSuppressed(_apply);
}

/** Revert to the stock royal-blue theme (e.g. on logout). */
export function clearBrand() {
  _branding = null;
  if (typeof document !== "undefined") {
    BRAND_VARS.forEach((v) => document.documentElement.style.removeProperty(v));
    SURFACE_VARS.forEach((v) => document.documentElement.style.removeProperty(v));
    const tag = document.getElementById(CSS_TAG_ID);
    if (tag) tag.remove();
  }
}

/**
 * Public: an AA-safe CSS colour for a solid fill that carries WHITE text, as `#rrggbb`.
 *
 * The marketing palette's `deep` shades are documented as "AA-safe … white text" but were never
 * verified: white on the fees `#EA580C` measures 3.56:1, under the 4.5:1 axe applies to normal text.
 * Any surface painting white on a palette colour must route through this instead of trusting the
 * token — `aaFill` darkens toward black until it clears TARGET, and always converges (black = 21:1).
 * Returns the input unchanged when it is not a 6-digit hex, so a CSS var / gradient passes through.
 */
export function aaFillCss(hex) {
  if (!isHex(hex)) return hex;
  const [r, g, b] = aaFill(hexToRgb(hex));
  return `#${[r, g, b].map((c) => c.toString(16).padStart(2, "0")).join("")}`;
}

// Exposed for tests: AA-corrected primary fill for a hex + the white-on-fill contrast ratio it achieves.
export const _aaFillHex = (hex) => triplet(aaFill(hexToRgb(hex)));
export const _whiteContrastOnFill = (hex) => contrast(WHITE, aaFill(hexToRgb(hex)));
export const _aaSurfaceHex = (hex, dark = false) => triplet(aaSurface(hexToRgb(hex), dark));
export const _stockBrandSurfaces = (theme) => {
  const stock = STOCK_SURFACES[theme];
  if (!stock) return null;
  return Object.fromEntries(Object.entries(stock).map(([key, value]) => [
    key,
    value.map((item) => (Array.isArray(item) ? item.slice() : item)),
  ]));
};
